import React, { useState, useEffect } from "react"
import { Helmet } from "react-helmet"
import { useDispatch } from "react-redux"
import { setBreadcrumb, setLoader, setTitle } from "store/actions"
import { ContentWrapper } from "components"
import { notification } from "antd"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"

import "./styles.scss"
import ImportModal from "./components/ImportModal"
import PendingUsersTable from "./components/PendingUsersTable"
import AddressValidationModal from "./components/AddressValidationModal"

const MasterData: React.FC = () => {
	const dispatch = useDispatch()

	// State variables
	const [defaultBusinesses, setDefaultBusinesses] = useState<any[]>([])
	const [showImportModal, setShowImportModal] = useState<boolean>(false)
	const [showAddressValidationModal, setShowAddressValidationModal] =
		useState<string>("")
	const [masterData, setMasterData] = useState<any[]>([])

	// Set up the breadcrumb for the page
	useEffect(() => {
		dispatch(
			setBreadcrumb(() => ({
				locations: [
					{
						icon: "settings",
						text: Strings.sidebar.settings,
						route: "/settings",
					},
					{
						text: Strings.settings.masterData,
						icon: "upload-file",
					},
				],
				actions: [],
			}))
		)
	}, [dispatch])

	// Set page title
	useEffect(() => {
		dispatch(setTitle(Strings.settings.masterData))
	}, [dispatch])

	// Get initial data for businesses
	useEffect(() => {
		getBusinesses(dispatch, setDefaultBusinesses)
	}, [dispatch])

	// Get pending and approved users
	useEffect(() => {
		getMasterData(dispatch, setMasterData)
	}, [dispatch])

	const refreshData = () => {
		getMasterData(dispatch, setMasterData)
	}

	return (
		<div className="ScreenMasterData">
			<Helmet>
				<title>{Strings.settings.masterData}</title>
				<meta
					name="description"
					content="Upload and manage master data files with address validation"
				/>
			</Helmet>
			<ContentWrapper>
				<PendingUsersTable
					users={masterData}
					setShowImportModal={setShowImportModal}
					setShowAddressValidationModal={setShowAddressValidationModal}
					refreshData={refreshData}
				/>

				<ImportModal
					showImportModal={showImportModal}
					setShowImportModal={setShowImportModal}
					businesses={defaultBusinesses}
					refreshData={refreshData}
				/>

				<AddressValidationModal
					userId={showAddressValidationModal}
					setShowAddressValidationModal={setShowAddressValidationModal}
					users={masterData}
					refreshData={refreshData}
				/>
			</ContentWrapper>
		</div>
	)
}

async function getBusinesses(
	dispatch: any,
	setDefaultBusinesses: (businesses: any[]) => void
) {
	dispatch(setLoader(true))

	try {
		const response = await API.get({
			url: Endpoints.uriBusinesses(),
		})

		if (response.ok) {
			const { business: businesses = [] } = response.data.results || {}
			setDefaultBusinesses(businesses)
		} else {
			notification.error({
				message: Strings.serverErrors.title,
				description: response.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}
	} catch (err) {
		notification.error({
			message: Strings.serverErrors.title,
			description: Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	}

	dispatch(setLoader(false))
}

async function getMasterData(
	dispatch: any,
	setMasterData: (users: any[]) => void
) {
	dispatch(setLoader(true))

	try {
		const response = await API.get({
			url: Endpoints.uriMasterData(),
		})

		if (response.ok) {
			const results = response.data.results || {}
			setMasterData(results?.data || [])
		} else {
			notification.error({
				message: Strings.settings.masterData,
				description: response.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}
	} catch (err) {
		notification.error({
			message: Strings.serverErrors.title,
			description: Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	}

	dispatch(setLoader(false))
}

export default MasterData
